// @ts-nocheck — slated for deletion
/* eslint-disable */
import { useMemo, useState } from "react"

import { MONTHS } from "@/utils/constants"
import { DateTime, Interval } from "luxon"

import Box from "@mui/material/Box"
import FormControl from "@mui/material/FormControl"
import FormGroup from "@mui/material/FormGroup"
import FormHelperText from "@mui/material/FormHelperText"
import Select from "@mui/material/Select"
import type { SelectChangeEvent } from "@mui/material/Select"
import { useTheme } from "@mui/material/styles"

const YEAR_DEFAULT_TEXT = "Year"
const FIRST_MONTH_DEFAULT_TEXT = "Start Month"
const SECOND_MONTH_DEFAULT_TEXT = "End Month"
const placeholderValue = -1

const getYearOptions = (availableMaxMinMonths: Interval | null): string[] => {
  if (availableMaxMinMonths.isValid) {
    const startYear = availableMaxMinMonths.start.year
    const endYear = availableMaxMinMonths.end.year

    return Array.from({ length: endYear - startYear + 1 }, (_, index) =>
      (startYear + index).toString()
    )
  }

  return []
}

const monthIsBeforeAvailableMonth = (
  availableMaxMinMonths: Interval,
  newSelectedYear: number,
  newMonth: number
): boolean =>
  newSelectedYear === availableMaxMinMonths.start.year &&
  newMonth < availableMaxMinMonths.start.month

const monthIsAfterAvailableMonth = (
  availableMaxMinMonths: Interval,
  newSelectedYear: number,
  newMonth: number
): boolean =>
  newSelectedYear === availableMaxMinMonths.end.year &&
  availableMaxMinMonths.end.month < newMonth

interface MonthRangeSelectorProps {
  availableMaxMinMonths?: Interval
  disabled?: boolean
  onChange: (value: Interval, context: undefined) => void
  value: Interval
}

/**
 * @deprecated Use MonthRangePicker component from nzds
 */
export const MonthRangeSelector = ({
  availableMaxMinMonths,
  value,
  onChange,
  disabled = false,
}: MonthRangeSelectorProps) => {
  const theme = useTheme()
  const styles = useMemo(
    () => ({
      monthSelectContainer: {
        width: theme.typography.pxToRem(128),
      },
      yearSelectContainer: {
        marginRight: 2,
        width: theme.typography.pxToRem(80),
      },
    }),
    [theme]
  )

  const [internalSelectedYear, setInternalSelectedYear] = useState(
    value.start?.isValid ? value.start.year : placeholderValue
  )
  const [internalSelectedStartMonth, setInternalSelectedStartMonth] = useState(
    value.start?.isValid ? value.start.month : placeholderValue
  )
  const [internalSelectedEndMonth, setInternalSelectedEndMonth] = useState(
    value.end?.isValid ? value.end.month : placeholderValue
  )

  const selectedYear = value.isValid ? value.start.year : internalSelectedYear
  const selectedStartMonth = value.isValid
    ? value.start.month
    : internalSelectedStartMonth
  const selectedEndMonth = value.isValid
    ? value.end.month
    : internalSelectedEndMonth

  const yearOptions = useMemo(
    () => getYearOptions(availableMaxMinMonths),
    [availableMaxMinMonths]
  )

  const isDisabled = useMemo(
    () => disabled || !availableMaxMinMonths.isValid,
    [availableMaxMinMonths, disabled]
  )

  const trySetStartEndMonth = (newSelectedYear: number): void => {
    let newStartMonth = selectedStartMonth

    if (
      monthIsBeforeAvailableMonth(
        availableMaxMinMonths,
        newSelectedYear,
        newStartMonth
      ) ||
      monthIsAfterAvailableMonth(
        availableMaxMinMonths,
        newSelectedYear,
        newStartMonth
      )
    ) {
      newStartMonth = placeholderValue
      setInternalSelectedStartMonth(newStartMonth)
    }

    let newEndMonth = selectedEndMonth

    if (
      monthIsAfterAvailableMonth(
        availableMaxMinMonths,
        newSelectedYear,
        newEndMonth
      ) ||
      monthIsBeforeAvailableMonth(
        availableMaxMinMonths,
        newSelectedYear,
        newEndMonth
      )
    ) {
      newEndMonth = placeholderValue
      setInternalSelectedEndMonth(newEndMonth)
    }

    const newDateRange = Interval.fromDateTimes(
      DateTime.fromObject({
        year: newSelectedYear,
        month: newStartMonth,
        day: 1,
      }),
      DateTime.fromObject({
        year: newSelectedYear,
        month: newEndMonth,
        day: 1,
      }).endOf("month")
    )

    if (newDateRange) {
      onChange(newDateRange, undefined)
    }
  }

  const onYearChange = (e: SelectChangeEvent): void => {
    const newSelectedYear = Number(e.target.value)
    setInternalSelectedYear(newSelectedYear)
    trySetStartEndMonth(newSelectedYear)
  }

  const onStartMonthChange = (e: SelectChangeEvent<number>): void => {
    setInternalSelectedStartMonth(Number(e.target.value))

    const newDateRange = Interval.fromDateTimes(
      DateTime.fromObject({
        year: selectedYear,
        month: Number(e.target.value),
        day: 1,
      }),
      DateTime.fromObject({
        year: selectedYear,
        month: selectedEndMonth,
        day: 1,
      }).endOf("month")
    )

    if (newDateRange.isValid) {
      onChange(newDateRange, undefined)
    }
  }

  const onEndMonthChange = (e: SelectChangeEvent<number>): void => {
    setInternalSelectedEndMonth(Number(e.target.value))

    const newDateRange = Interval.fromDateTimes(
      DateTime.fromObject({
        year: selectedYear,
        month: selectedStartMonth,
        day: 1,
      }),
      DateTime.fromObject({
        year: selectedYear,
        month: Number(e.target.value),
        day: 1,
      }).endOf("month")
    )

    if (newDateRange.isValid) {
      onChange(newDateRange, undefined)
    }
  }

  const isStartMonthUnavailable: boolean =
    selectedYear !== placeholderValue && selectedStartMonth === placeholderValue

  const isEndMonthUnavailable: boolean =
    selectedYear !== placeholderValue && selectedEndMonth === placeholderValue

  return (
    <FormGroup row sx={{ alignItems: "baseline" }}>
      <FormControl disabled={isDisabled} size="small">
        <Select
          inputProps={{
            "aria-label": "year",
            "data-testid": "year-select",
            "data-e2e": "year-select",
          }}
          native
          onChange={onYearChange}
          sx={styles.yearSelectContainer}
          value={selectedYear.toString()}
        >
          <option key={YEAR_DEFAULT_TEXT} disabled value={placeholderValue}>
            {YEAR_DEFAULT_TEXT}
          </option>
          {yearOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Select>
      </FormControl>
      <FormGroup sx={{ ...styles.monthSelectContainer, marginRight: 2 }}>
        <FormControl disabled={isDisabled} size="small">
          <Select
            inputProps={{
              "aria-label": "start month",
              "data-testid": "first-month-select",
              "data-e2e": "first-month-select",
            }}
            native
            onChange={onStartMonthChange}
            value={selectedStartMonth}
          >
            <option
              key={FIRST_MONTH_DEFAULT_TEXT}
              disabled
              value={placeholderValue}
            >
              {FIRST_MONTH_DEFAULT_TEXT}
            </option>
            {[...Array(12).keys()].map((option) => {
              const monthNumber = option + 1

              return (
                <option
                  key={monthNumber}
                  disabled={
                    !availableMaxMinMonths.isValid ||
                    (selectedYear === availableMaxMinMonths.start.year &&
                      monthNumber < availableMaxMinMonths.start.month) ||
                    (selectedEndMonth !== placeholderValue &&
                      selectedEndMonth < monthNumber) ||
                    yearOptions?.length === 0
                  }
                  value={monthNumber}
                >
                  {MONTHS[option]}
                </option>
              )
            })}
          </Select>
          {isStartMonthUnavailable && (
            <FormHelperText data-testid="first-month-error-text">
              Previous selection unavailable
            </FormHelperText>
          )}
        </FormControl>
      </FormGroup>
      <Box
        component="span"
        sx={{
          mr: 2,
        }}
      >
        —
      </Box>
      <FormGroup sx={styles.monthSelectContainer}>
        <FormControl disabled={isDisabled} size="small">
          <Select
            inputProps={{
              "aria-label": "end month",
              "data-testid": "second-month-select",
              "data-e2e": "second-month-select",
            }}
            native
            onChange={onEndMonthChange}
            value={selectedEndMonth}
          >
            <option
              key={SECOND_MONTH_DEFAULT_TEXT}
              disabled
              value={placeholderValue}
            >
              {SECOND_MONTH_DEFAULT_TEXT}
            </option>
            {[...Array(12).keys()].map((option) => {
              const monthNumber = option + 1

              return (
                <option
                  key={monthNumber}
                  disabled={
                    !availableMaxMinMonths.isValid ||
                    (selectedYear === availableMaxMinMonths.end.year &&
                      availableMaxMinMonths.end.month < monthNumber) ||
                    selectedStartMonth === placeholderValue ||
                    selectedStartMonth > monthNumber ||
                    yearOptions?.length === 0
                  }
                  value={monthNumber}
                >
                  {MONTHS[option]}
                </option>
              )
            })}
          </Select>
          {isEndMonthUnavailable && (
            <FormHelperText data-testid="second-month-error-text">
              Previous selection unavailable
            </FormHelperText>
          )}
        </FormControl>
      </FormGroup>
    </FormGroup>
  )
}
